import produce from 'immer';
import { useCallback, useMemo } from 'react';
import { uuid } from 'short-uuid';

import { FileType } from '@work4all/models/lib/File';
import { LayoutTypes } from '@work4all/models/lib/Layout';

import { settings, useSetting } from '../../../../../settings';

export const fileToLayoutType = {
  [FileType.CUSTOMER]: LayoutTypes.Customer,
  [FileType.PROJECT]: LayoutTypes.Project,
  [FileType.SUPPLIER]: LayoutTypes.Supplier,
};

export const useFileWidgetSettings = (fileType: FileType) => {
  const layoutType = fileToLayoutType[fileType];

  const filePageWidgetLayoutDefinitions = useSetting(
    settings.filePageWidgetLayoutDefinitions(layoutType)
  );

  const activeFilePageWidgetLayoutDefinitionId = useSetting(
    settings.activeFilePageWidgetLayoutDefinitionId()
  );

  const activeDefId = activeFilePageWidgetLayoutDefinitionId.value[layoutType];

  const hiddenWidgets = useSetting(
    settings.hiddenWidgets({
      fileType,
      layoutId: activeDefId,
    })
  );

  const collapsedWidgets = useSetting(
    settings.collapsedWidgets({
      fileType,
      layoutId: activeDefId,
    })
  );

  const persistedFileWidgetLayouts = useSetting(
    settings.fileWidgetLayouts(activeDefId)
  );

  const filePageWidgetGroupRegister = useSetting(
    settings.filePageWidgetGroupRegister(activeDefId)
  );

  const filePageDeletedWidgetGroups = useSetting(
    settings.filePageDeletedWidgetGroups(activeDefId)
  );

  const filePageWidgetGroupNameRegister = useSetting(
    settings.filePageWidgetGroupNameRegister(activeDefId)
  );

  const filePageAdditionalWidgetGroups = useSetting(
    settings.filePageAdditionalWidgetGroups(activeDefId)
  );

  const activeLayoutDefinition = useMemo(
    () =>
      filePageWidgetLayoutDefinitions.value.find((x) => x.id === activeDefId),
    [activeDefId, filePageWidgetLayoutDefinitions.value]
  );

  const addLayoutDefinition = useCallback(() => {
    filePageWidgetLayoutDefinitions.set([
      ...filePageWidgetLayoutDefinitions.value,
      {
        id: uuid(),
        name: 'Layout ' + (filePageWidgetLayoutDefinitions.value.length + 1),
      },
    ]);
  }, [filePageWidgetLayoutDefinitions]);

  const deleteLayoutDefinition = useCallback(
    async (id: string) => {
      if (!id) {
        return;
      }
      filePageWidgetLayoutDefinitions.set(
        filePageWidgetLayoutDefinitions.value.filter((x) => x.id !== id)
      );

      if (activeDefId === id) {
        activeFilePageWidgetLayoutDefinitionId.set({
          ...activeFilePageWidgetLayoutDefinitionId.value,
          [layoutType]: null,
        });
      }
    },
    [
      activeDefId,
      activeFilePageWidgetLayoutDefinitionId,
      filePageWidgetLayoutDefinitions,
      layoutType,
    ]
  );

  const editLayoutDefinition = useCallback(
    (value: { id: string; name: string }) => {
      if (!value.id) {
        return;
      }

      const newLayoutDefinitions = [...filePageWidgetLayoutDefinitions.value];
      newLayoutDefinitions.find((x) => x.id === value.id).name = value.name;

      filePageWidgetLayoutDefinitions.set(newLayoutDefinitions);
    },
    [filePageWidgetLayoutDefinitions]
  );

  const addWidgetGroup = useCallback(
    (name: string) => {
      filePageAdditionalWidgetGroups.set([
        ...filePageAdditionalWidgetGroups.value,
        {
          id: uuid(),
          name,
          layoutType,
        },
      ]);
    },
    [filePageAdditionalWidgetGroups, layoutType]
  );

  const deleteWidgetGroups = useCallback(
    (ids: string[]) => {
      filePageAdditionalWidgetGroups.set(
        filePageAdditionalWidgetGroups.value.filter(
          (item) => !ids.includes(item.id)
        )
      );
    },
    [filePageAdditionalWidgetGroups]
  );

  const editWidgetGroup = useCallback(
    (value: { id: string; name: string }) => {
      if (!value.id) {
        return;
      }

      const newLayoutDefinitions = produce(
        filePageAdditionalWidgetGroups.value,
        (draft) => {
          draft.find((x) => x.id === value.id).name = value.name;
        }
      );

      filePageAdditionalWidgetGroups.set(newLayoutDefinitions);
    },
    [filePageAdditionalWidgetGroups]
  );

  const setLayoutDefinitionId = useCallback(
    (value: {
      [LayoutTypes.Customer]?: string;
      [LayoutTypes.Supplier]?: string;
      [LayoutTypes.Project]?: string;
    }) => {
      activeFilePageWidgetLayoutDefinitionId.set({
        ...activeFilePageWidgetLayoutDefinitionId.value,
        ...value,
      });
    },
    [activeFilePageWidgetLayoutDefinitionId]
  );

  return {
    activeLayoutDefinition,
    layoutDefinitions: filePageWidgetLayoutDefinitions.value,
    addLayoutDefinition,
    setLayoutDefinitionId,
    deleteLayoutDefinition,
    editLayoutDefinition,
    hiddenWidgets,
    collapsedWidgets,
    persistedFileWidgetLayouts,
    filePageWidgetGroupRegister,
    filePageDeletedWidgetGroups,
    filePageWidgetGroupNameRegister,
    addWidgetGroup,
    deleteWidgetGroups,
    editWidgetGroup,
  };
};
